import { apiService } from '@app/shared/services/api.service';
import type {
  BillingInvoice,
  BillingInvoiceLine,
  BillingPayment,
  PayNowResponse,
  PrepaymentPreview,
} from '@neogeniux/api-contracts';

const BASE = '/billing/me';

export interface CustomerInvoiceBundle {
  invoice: BillingInvoice;
  lines: BillingInvoiceLine[];
  payments: BillingPayment[];
  /**
   * Identity of the billed business, as the backend resolves it.
   * Preferred over the auth store on the invoice PDF so the tenant's
   * copy and the sadmin copy of the same invoice print an identical
   * "Facturado a" block.
   */
  business: {
    name: string;
    taxId: string;
    address: string;
  } | null;
}

export interface FiscalDownloadResponse {
  /** Presigned S3 URL (stored-copy path: automated hook / backfill). */
  downloadUrl?: string;
  /**
   * Public share token (per-row link path). The FE builds the public
   * invoice URL from it (`buildPublicInvoiceUrl`) and opens that page —
   * the comprobante renders from the platform template, no stored object.
   */
  shareToken?: string;
  filename: string;
}

export interface BusinessFiscalPreference {
  wantsFiscalInvoices: boolean;
  taxId: string | null;
  legalName: string | null;
}

export interface NextInvoicePreviewInvoiceLine {
  lineType: string;
  itemKind: string | null;
  itemCode: string | null;
  sourceSubscriptionItemId: string | null;
  catalogEntityKind: string | null;
  catalogEntityCode: string | null;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  currency: string;
  sortOrder: number;
  /** True when the line is a sadmin cortesía billing at the
   *  compValueUsd override. FE renders the "Cortesía" badge +
   *  strikethrough `catalogListPrice` beside the actual `amount`. */
  isComplimentary?: boolean;
  /** Original catalog list price for the same qty/cadence — the
   *  "before" figure shown struck through. Null on regular lines. */
  catalogListPrice?: string | null;
  /** True when a complimentary line's (kind, code) already ships
   *  with the business's paid plan — renders "No aplicada" badge. */
  isRedundantWithPlan?: boolean;
}

export interface NextInvoicePreviewDisplayLine {
  itemKind: string;
  itemCode: string;
  description: string;
  isComplimentary: boolean;
  source: string;
  /** True when (itemKind, itemCode) already ships with the paid
   *  plan template — Cortesía is inert. */
  isRedundantWithPlan?: boolean;
}

export interface NextInvoicePreview {
  subscriptionId: string;
  currency: string;
  billingInterval: 'monthly' | 'annual';
  periodStart: string | null;
  periodEnd: string | null;
  renewalDate: string | null;
  subtotal: string;
  taxTotal: string;
  total: string;
  annualEstimate: string;
  invoiceLines: NextInvoicePreviewInvoiceLine[];
  displayLines: NextInvoicePreviewDisplayLine[];
}

/**
 * Row shape returned by GET /billing/me/payment-history — unified
 * feed of billing_payment + billing_collection_attempt + scheduled
 * billing_invoice rows. Mirrors the service's mapping shape in
 * apps/neoapi/src/modules/billing/billing.service.ts:getPaymentHistoryForBusiness.
 */
export interface PaymentHistoryRow {
  kind: 'success' | 'declined' | 'refund' | 'scheduled';
  date: string;
  shortId: string;
  id: string;
  method: {
    provider: string | null;
    label: string;
    cardBrand?: string | null;
    cardLast4?: string | null;
  };
  amount: string;
  currency: string;
  /** Null on the synthetic "next-payment estimate" row — cron hasn't
   *  materialized the invoice yet. FE hides the invoice-download +
   *  receipt icons when null. */
  invoiceId: string | null;
  invoiceNumber: string | null;
  receiptId?: string | null;
  failureReason?: string | null;
  failureKind?: string | null;
  /** Scheduled next retry from `billing_collection_attempt.next_attempt_at`.
   *  Only populated on `declined` rows. Null when the retry chain is
   *  exhausted (no more retries queued). */
  nextAttemptAt?: string | null;
  dueDate?: string | null;
  /**
   * When the underlying billing_invoice was GENERATED
   * (`billing_invoice.issued_at`) — rendered as the "Emitida" column.
   * Distinct from `date`, which on a scheduled row IS the due date
   * and therefore duplicated the "Vence …" caption under Estado.
   * Null on the synthetic preview row (nothing issued yet).
   */
  issuedAt?: string | null;
  /** True when this row represents a synthetic preview (next-invoice
   *  estimate) instead of a real billing_invoice row. */
  isPreview?: boolean;
  /** True when the underlying billing_invoice has a fiscal
   *  comprobante attached. FE only renders the Factura-column
   *  download icon when this is true. */
  hasComprobante?: boolean;
  /** True when the underlying invoice is settled (any succeeded
   *  billing_payment exists). FE hides the Pagar button on
   *  historical declined rows once this flips. */
  invoicePaid?: boolean;
  /** Backend-owned: true on exactly one row per unpaid invoice — the
   *  row that carries the Pagar action. */
  canPay?: boolean;
}

export const customerBillingService = {
  listInvoices: (): Promise<BillingInvoice[]> =>
    apiService.getInstance().get(`${BASE}/invoices`),

  getPaymentHistory: (): Promise<PaymentHistoryRow[]> =>
    apiService.getInstance().get(`${BASE}/payment-history`),

  getFiscalPreference: (): Promise<BusinessFiscalPreference> =>
    apiService.getInstance().get(`${BASE}/fiscal-preference`),

  setBusinessFiscalPreference: (
    wantsFiscalInvoice: boolean,
    identity?: { taxId?: string; legalName?: string }
  ): Promise<{
    wantsFiscalInvoices: boolean;
    updatedOpenInvoices: number;
    lockedInvoices: number;
    retroInvoiceIssued: boolean;
  }> =>
    apiService.getInstance().patch(`${BASE}/fiscal-preference`, {
      wantsFiscalInvoice,
      ...identity,
    }),

  getInvoice: (id: string): Promise<CustomerInvoiceBundle> =>
    apiService.getInstance().get(`${BASE}/invoices/${id}`),

  setFiscalPreference: (
    id: string,
    wantsFiscalInvoice: boolean
  ): Promise<BillingInvoice> =>
    apiService.getInstance().patch(`${BASE}/invoices/${id}/fiscal-preference`, {
      wantsFiscalInvoice,
    }),

  getFiscalAttachment: (id: string): Promise<FiscalDownloadResponse> =>
    apiService.getInstance().get(`${BASE}/invoices/${id}/fiscal-attachment`),

  /**
   * Server-side single source of truth for the next-invoice preview.
   * Returns billable invoice lines + Cortesía display lines + totals
   * + renewal date; FE renders directly (no re-derivation).
   */
  getNextInvoicePreview: (): Promise<NextInvoicePreview> =>
    apiService.getInstance().get(`${BASE}/next-invoice-preview`),

  /**
   * Kicks off a Stripe/PayPal PaymentIntent for the invoice; the
   * returned `clientSecret` feeds Stripe Elements on the FE which
   * then confirms with the customer's card. Webhook closes the loop
   * server-side by flipping the invoice to `paid`.
   */
  startInvoicePayment: (
    id: string,
    provider: 'stripe' | 'paypal',
    opts?: { saveCard?: boolean; sessionNonce?: string }
  ): Promise<{
    intent: {
      providerPaymentId: string;
      clientSecret?: string | null;
      provider: 'stripe' | 'paypal';
    };
    payment: BillingPayment;
    /** False when Stripe customer creation failed on the backend
     *  (network / API key / detached-customer). FE disables the
     *  save-card checkbox + shows a hint — without a customer the
     *  confirm-time override silently no-ops and the operator
     *  would think they saved a card that didn't attach. */
    saveCardAvailable?: boolean;
  }> =>
    apiService.getInstance().post(`${BASE}/invoices/${id}/pay`, {
      provider,
      // Only send when true — keeps the wire payload minimal for
      // the default one-off path.
      ...(opts?.saveCard ? { saveCard: true } : {}),
      // Per-modal-session nonce → new PaymentIntent per open cycle.
      // Prevents Stripe from returning a cancelled intent (terminal
      // state → Elements mount fails).
      ...(opts?.sessionNonce ? { sessionNonce: opts.sessionNonce } : {}),
    }),

  /**
   * Best-effort cancel of a not-yet-confirmed PaymentIntent. Called
   * on pay-modal close so the double-charge guard doesn't treat the
   * abandoned pending row as in-flight on the next re-open.
   */
  cancelInvoicePayment: (
    invoiceId: string,
    paymentId: string
  ): Promise<{ cancelled: boolean }> =>
    apiService
      .getInstance()
      .post(`${BASE}/invoices/${invoiceId}/pay/${paymentId}/cancel`, {}),

  /**
   * Synchronous settle after `stripe.confirmPayment` returns
   * succeeded. Backend re-verifies with Stripe (never trusts the
   * FE-supplied status) and flips billing_payment + invoice to
   * paid state. Faster than waiting for the webhook + a safety
   * net for envs where the webhook is misconfigured.
   */
  settleInvoicePayment: (
    invoiceId: string,
    paymentId: string,
    opts?: { saveCard?: boolean }
  ): Promise<{ settled: boolean; cardAttached: boolean }> =>
    apiService
      .getInstance()
      .post(`${BASE}/invoices/${invoiceId}/pay/${paymentId}/settle`, {
        ...(opts?.saveCard ? { saveCard: true } : {}),
      }),

  /**
   * One-click off-session charge on the saved default card. No card
   * entry — Stripe pulls from the subscription's saved payment
   * method. Returns immediately with either `succeeded` (invoice
   * flipped to paid) or `requires_action` (3DS pending, Stripe
   * emails the customer) or `failed` (Stripe error, invoice → failed).
   */
  chargeInvoiceWithSavedCard: (
    id: string
  ): Promise<{
    invoice: BillingInvoice;
    payment: BillingPayment;
    status: 'succeeded' | 'requires_action' | 'failed';
    reason?: string | null;
  }> =>
    apiService.getInstance().post(`${BASE}/invoices/${id}/charge-saved`, {}),

  /**
   * "Pay Now (with optional N-month prepayment)" — materializes the
   * next recurring invoice + charges the caller's saved default
   * card off-session in one call. `monthsCount` defaults to 1 on the
   * backend; valid values 1 / 3 / 6 / 12. For an `annual` sub the
   * parameter is ignored.
   *
   * On succeeded: response carries the paid invoice + subscription
   * advanced by N months.
   * On requires_action: response carries the pending payment; Stripe
   * emails the customer to complete 3DS.
   * On failed: server returns HTTP 402 with `errorCode:'PREPAYMENT_CARD_FAILED'`
   * — the axios interceptor throws; caller handles via getTranslatedErrorMessage.
   */
  payNow: (monthsCount?: number): Promise<PayNowResponse> =>
    apiService.getInstance().post(`${BASE}/recurring/pay-now`, {
      ...(monthsCount !== undefined ? { monthsCount } : {}),
    }),

  /**
   * Dry-run preview of what the caller WOULD pay if they clicked
   * "Pay Now" with `monthsCount`. Zero DB writes. Powers the FE's
   * live-total button label.
   */
  getPrepaymentPreview: (monthsCount: number): Promise<PrepaymentPreview> =>
    apiService.getInstance().get(`${BASE}/recurring/prepayment-preview`, {
      params: { monthsCount },
    }),
};
